import { ISocialLogInApiResponseProps } from '../api/social';
import {
    GA_ACCOUNT_AUTOLINKED,
    GA_EMAIL_NOT_FOUND,
    GA_FACEBOOK,
    GA_HIDDEN_EMAIL,
    GA_LINK_ACCOUNT_OPTION,
    GA_SHOWED_EMAIL,
    GA_SIWA_ACCOUNTS_LINK_OPTION,
    GA_SIWA_EMAIL_ALREADY_EXISTS,
    GA_SOCIAL_ACCOUNT_CREATED,
    GA_SOCIAL_SIGNUP_EVENT,
    LOGIN_METHOD_FACEBOOK,
} from '../constants/analytics';
import { trackLoginSuccessInHeap } from '../tracking/heapTracking';
import {
    trackGeneralAuthAction,
    trackGeneralPageUnloadAuthAction,
} from './general';
import { IAnalyticsDimensionsProps } from './interfaces';
import {
    trackLoginClicked,
    trackLoginSuccessful,
    trackLoginSuccessOneStep,
} from './login';
import { capitalize } from './utils';

export const trackSocialProviderClick = ({
    socialProvider,
    stateFromProvider,
    analytics,
}: {
    socialProvider: string;
    stateFromProvider: any;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackLoginClicked({
        sourceProvider: socialProvider,
        stateFromProvider,
        analytics,
    });
};

export const trackAccountAutolinked = ({
    socialProvider,
    analytics,
    extraInfo = '',
}: {
    socialProvider: string;
    analytics?: IAnalyticsDimensionsProps;
    extraInfo?: string;
}) => {
    trackSocialAction({
        action: GA_ACCOUNT_AUTOLINKED + extraInfo,
        socialProvider,
        analytics,
    });
};

export const trackSocialLogInSuccess = ({
    response,
    socialProvider,
    analytics,
}: {
    response: any;
    socialProvider: string;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    if (response.newUser ?? response['new_user']) {
        trackSocialAccountCreated({ socialProvider, analytics });

        trackLoginSuccessful({
            socialProvider,
            analytics,
        });
        trackLoginSuccessInHeap({ loginMethod: socialProvider });
    } else {
        trackLoginSuccessOneStep({
            socialProvider,
            analytics,
        });
    }
};

export const trackSocialAccountCreated = ({
    analytics,
    socialProvider,
}: {
    socialProvider: string;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackSocialAction({
        action: GA_SOCIAL_ACCOUNT_CREATED[socialProvider],
        socialProvider,
        analytics,
    });
};

export const trackSocialError = ({
    analytics,
    socialProvider,
    action,
}: {
    socialProvider?: string;
    analytics?: IAnalyticsDimensionsProps;
    action: string;
}) => {
    const labelObject = socialProvider ? { label: socialProvider } : {};

    trackGeneralAuthAction({ action, analytics, ...labelObject });
};

export const trackSocialAction = ({
    action,
    socialProvider,
    analytics,
}: {
    action: string;
    socialProvider: string;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    const label = capitalize(socialProvider);

    trackGeneralAuthAction({ action, label, analytics });
};

export const trackSIWA_AccountsLinkOptionWithHiddenEmail = ({
    hidden,
    analytics,
}: {
    hidden: boolean;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackGeneralAuthAction({
        analytics,
        action: GA_SIWA_ACCOUNTS_LINK_OPTION,
        label: hidden ? GA_HIDDEN_EMAIL : GA_SHOWED_EMAIL,
    });
};

export const trackSIWA_AccountLinkAttemptWithHiddenEmail = ({
    analytics,
}: {
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackGeneralAuthAction({
        analytics,
        action: GA_SIWA_EMAIL_ALREADY_EXISTS,
        label: GA_LINK_ACCOUNT_OPTION,
    });
};

export const trackSIWA_EmailNotFound = ({
    analytics,
}: {
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackGeneralAuthAction({
        analytics,
        action: GA_SIWA_EMAIL_ALREADY_EXISTS,
        label: GA_EMAIL_NOT_FOUND,
    });
};

export const trackFBLoginSuccess = ({
    response,
    analytics,
}: {
    response: ISocialLogInApiResponseProps;
    analytics?: IAnalyticsDimensionsProps;
}) => {
    if (response.newUser) {
        trackSocialAccountCreated({ analytics, socialProvider: GA_FACEBOOK });

        trackLoginSuccessful({
            socialProvider: GA_FACEBOOK,
            analytics,
        });
        trackLoginSuccessInHeap({ loginMethod: LOGIN_METHOD_FACEBOOK });
    } else {
        trackLoginSuccessOneStep({
            socialProvider: GA_FACEBOOK,
            analytics,
        });
    }
};

export const trackSocialSignupEvent = ({
    analytics,
}: {
    analytics?: IAnalyticsDimensionsProps;
}) => {
    trackGeneralPageUnloadAuthAction({
        action: GA_SOCIAL_SIGNUP_EVENT,
        analytics,
    });
};
